/**
 * Note generation error presets.
 *
 * To add a new preset:
 * 1. Add a new key to the NoteGenerationErrorPreset union in types.ts (e.g. 'MY_ERROR').
 * 2. Export a constant for consumers: e.g. export const MY_ERROR = { preset: 'MY_ERROR' as const }.
 * 3. Add the preset content (title, intro, listItems, tryThis) to NOTE_GENERATION_ERROR_PRESETS below.
 * 4. Where the error is detected, call setError(MY_ERROR) or setProcessingError({ preset: 'MY_ERROR' }).
 * 5. (Optional) Export the new constant from index.ts so it's available from @repo/videa-notes.
 */

import type {
  NoteGenerationErrorPreset,
  StructuredPresetContent,
} from './types';

/** Props to pass to NoteGenerationError for the empty-transcript preset. */
export const EMPTY_TRANSCRIPT_ERROR = {
  preset: 'EMPTY_TRANSCRIPT' as const,
} as const;

/** Props to pass to NoteGenerationError when no specific error is available (fallback). */
export const GENERIC_ERROR = {
  preset: 'GENERIC' as const,
} as const;

export const NOTE_GENERATION_ERROR_PRESETS: Record<
  NoteGenerationErrorPreset,
  StructuredPresetContent
> = {
  EMPTY_TRANSCRIPT: {
    title:
      'No audio detected in recording, please check that your microphone is unmuted.',
    intro:
      "We couldn't find any speech in your recording. This usually happens when:",
    listItems: [
      'Your microphone is muted or disconnected',
      'The recording was stopped before speaking began',
    ],
    tryThis:
      'Check that your microphone is unmuted and properly connected, then start a new recording.',
  },
  GENERIC: {
    title: 'Something went wrong',
    intro: 'An unexpected error occurred.',
    listItems: [],
    tryThis: 'Please try again later.',
  },
};
